import type { CallMetricsView, EvidenceRef, FindingView, Me, ReasonCategoryView, ReviewView, SaleOutcome } from '@smarton-pbx/shared';
import { Flag, Loader2, Pencil, RefreshCw, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '../api';
import { Notice, Pill } from '../components/ui';
import { t, tc } from '../i18n';

const clock = (ms: number) => {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

const categoryNames: Record<string, string> = {
  SALES: t('Sales call'),
  SUPPORT: t('Support call'),
  INTERNAL: t('Between colleagues'),
  NOT_A_CONVERSATION: t('Not a conversation'),
};

const skipReasons: Record<string, string> = {
  internal: t('Calls between colleagues aren’t reviewed.'),
  'not-answered': t('Nobody answered, so there was nothing to review.'),
  'too-short': t('The call was too short to review.'),
  'no-speech': t('No speech was found in the call.'),
  budget: t('This month’s AI budget is used up; reviews start again next month.'),
  off: t('AI reviews were turned off before this call was reviewed.'),
};

const outcomeKinds: Record<SaleOutcome, 'success' | 'warning' | 'danger' | undefined> = {
  SALE_AGREED: 'success',
  FOLLOW_UP_AGREED: 'warning',
  NO_SALE: 'danger',
  NOT_SALES: undefined,
};

/** A moment in a call the finding is based on: plays it here, or opens that call. */
function EvidenceChips({ evidence, callId, onPlay }: { evidence: EvidenceRef[]; callId: string; onPlay: (ms: number) => void }) {
  return (
    <span className="evidence">
      {evidence.slice(0, 4).map((e) =>
        e.callId === callId ? (
          <button key={e.segmentId} type="button" className="chip-button" onClick={() => onPlay(e.startMs)} title={t('Play this moment')} data-testid="evidence">
            ▶ {clock(e.startMs)}
          </button>
        ) : (
          <Link key={e.segmentId} className="chip-button" to={`/calls/${e.callId}?t=${e.startMs}`} title={t('Open that call at this moment')} data-testid="evidence">
            ↗ {clock(e.startMs)}
          </Link>
        ),
      )}
    </span>
  );
}

function FindingItem({ finding, callId, onPlay }: { finding: FindingView; callId: string; onPlay: (ms: number) => void }) {
  const [flagged, setFlagged] = useState(finding.flagged);
  async function flag() {
    const note = window.prompt(t('What is wrong with it? (optional)')) ?? null;
    await api.markFindingWrong(finding.id, note?.trim() || null);
    setFlagged(true);
  }
  return (
    <li className={finding.removed ? 'removed' : ''} data-testid="finding" data-kind={finding.kind}>
      <div className="finding-text">
        {finding.name && <strong>{finding.name}: </strong>}
        {finding.text}
        {finding.category && (
          <>
            {' '}
            <Pill>{finding.category.name}</Pill>
          </>
        )}
        {finding.source === 'USER' && (
          <>
            {' '}
            <Pill kind="brand">{t('Corrected')}</Pill>
          </>
        )}
      </div>
      {finding.detail && <div className="finding-detail">{t('Handled: {detail}', { detail: finding.detail })}</div>}
      <div className="finding-meta">
        <EvidenceChips evidence={finding.evidence} callId={callId} onPlay={onPlay} />
        {finding.source === 'AI' &&
          (flagged ? (
            <span className="subtle">{t('Marked as wrong')}</span>
          ) : (
            <button type="button" className="ghost small" onClick={() => void flag()} title={t('Tell us this point is wrong')} data-testid="mark-wrong">
              <Flag size={13} aria-hidden /> {t('Wrong?')}
            </button>
          ))}
      </div>
    </li>
  );
}

function ReasonEditor({ review, callId, onSaved }: { review: ReviewView; callId: string; onSaved: () => void }) {
  const [categories, setCategories] = useState<ReasonCategoryView[] | null>(null);
  const [chosen, setChosen] = useState<Set<string>>(
    new Set(review.findings.filter((f) => f.kind === 'reason' && !f.removed && f.category).map((f) => f.category!.id)),
  );
  const [open, setOpen] = useState(false);
  if (!open) {
    return (
      <button
        type="button"
        className="ghost small"
        onClick={() => {
          setOpen(true);
          void api.listCategories().then((list) => setCategories(list.filter((c) => c.status === 'ACTIVE')), () => setCategories([]));
        }}
        data-testid="edit-reasons"
      >
        <Pencil size={13} aria-hidden /> {t('Correct the reasons')}
      </button>
    );
  }
  return (
    <div className="reason-editor">
      {!categories ? (
        <Loader2 size={16} className="spin" aria-hidden />
      ) : (
        <div className="checks">
          {categories.map((c) => (
            <label key={c.id} className="checkbox">
              <input
                type="checkbox"
                checked={chosen.has(c.id)}
                onChange={(e) => {
                  const next = new Set(chosen);
                  if (e.target.checked) next.add(c.id);
                  else next.delete(c.id);
                  setChosen(next);
                }}
              />
              {c.name}
            </label>
          ))}
        </div>
      )}
      <div className="row" style={{ gap: '0.5rem' }}>
        <button type="button" className="small" onClick={() => void api.correctReasons(callId, [...chosen]).then(onSaved)} data-testid="save-reasons">
          {t('Save reasons')}
        </button>
        <button type="button" className="secondary small" onClick={() => setOpen(false)}>
          {t('Cancel')}
        </button>
      </div>
    </div>
  );
}

/** An AI review: of one call, or of a conversation across several calls. */
export function ReviewCard({
  review,
  callId,
  me,
  title,
  onPlay,
  onChanged,
}: {
  review: ReviewView;
  callId: string;
  me: Me;
  title: string;
  onPlay: (ms: number) => void;
  onChanged: () => void;
}) {
  const [error, setError] = useState<string>();
  const findings = review.findings.filter((f) => !f.removed);
  // The review type's words (the API's, in English).
  const label = (value: SaleOutcome | null) => {
    const name = review.outcomes.find((o) => o.value === value)?.label;
    return name ? t(name) : value;
  };

  if (review.status !== 'DONE') {
    return (
      <div className="card">
        <div className="card-header">
          <h2 className="row" style={{ gap: '0.5rem' }}>
            <Sparkles size={17} aria-hidden /> {title}
          </h2>
        </div>
        <div className="card-body" data-testid="review-status" data-status={review.status}>
          {review.status === 'PENDING' ? (
            <p className="muted row" style={{ gap: '0.5rem' }}>
              <Loader2 size={16} className="spin" aria-hidden /> {t('Reviewing the call…')}
            </p>
          ) : review.status === 'SKIPPED' ? (
            <p className="muted">{skipReasons[review.skipReason ?? ''] ?? t('Not reviewed.')}</p>
          ) : (
            <Notice kind="error">{t('The review failed. The call and its transcript are unaffected.')}</Notice>
          )}
        </div>
      </div>
    );
  }

  const scores = findings.filter((f) => f.kind === 'score');
  return (
    <div className="card" data-testid="review">
      <div className="card-header">
        <h2 className="row" style={{ gap: '0.5rem' }}>
          <Sparkles size={17} aria-hidden /> {title}
        </h2>
        <span className="row" style={{ gap: '0.4rem' }}>
          {review.category && <Pill>{categoryNames[review.category]}</Pill>}
          {review.outcome && (
            <span data-testid="review-outcome" data-outcome={review.outcome}>
              <Pill {...(outcomeKinds[review.outcome] && { kind: outcomeKinds[review.outcome]! })}>{label(review.outcome)}</Pill>
            </span>
          )}
        </span>
      </div>
      <div className="card-body stack review">
        {review.category !== 'SALES' ? (
          <p className="muted">{review.categoryNote ?? t('Only sales calls get a full review.')}</p>
        ) : (
          <>
            {review.summary && <p className="review-summary">{review.summary}</p>}

            {review.canCorrect && (
              <label className="field outcome-field">
                {tc('sale', 'Outcome')}
                <select
                  value={review.outcome ?? ''}
                  onChange={(e) =>
                    void api.correctOutcome(callId, e.target.value as SaleOutcome).then(onChanged, (err: unknown) => setError(String(err)))
                  }
                  data-testid="correct-outcome"
                >
                  {review.outcomes.map((o) => (
                    <option key={o.value} value={o.value}>
                      {t(o.label)}
                    </option>
                  ))}
                </select>
                <span className="field-hint">
                  {review.outcomeSource === 'USER'
                    ? t('Corrected by a person. The AI said: {outcome}.', { outcome: label(review.aiOutcome) ?? '' })
                    : t('The AI’s guess. Correct it if it’s wrong: corrections always count.')}
                </span>
              </label>
            )}

            {review.sections.map((section) => {
              const items = findings.filter((f) => f.kind === section.kind);
              if (!items.length && !(section.kind === 'reason' && review.canCorrect && review.outcome !== 'SALE_AGREED')) return null;
              return (
                <section key={section.kind} className="review-section">
                  <h3>{t(section.label)}</h3>
                  {items.length > 0 && (
                    <ul className="findings">
                      {items.map((f) => (
                        <FindingItem key={f.id} finding={f} callId={callId} onPlay={onPlay} />
                      ))}
                    </ul>
                  )}
                  {section.kind === 'reason' && review.canCorrect && <ReasonEditor review={review} callId={callId} onSaved={onChanged} />}
                </section>
              );
            })}

            {scores.length > 0 && (
              <section className="review-section">
                <h3>{t('Scorecard')}</h3>
                <ul className="findings">
                  {scores.map((f) => (
                    <li key={f.id}>
                      <div className="finding-text">
                        <strong>{review.scorecard.find((c) => c.key === f.criterion)?.name ?? f.criterion}:</strong> {f.score}/5. {f.text}
                      </div>
                      <div className="finding-meta">
                        <EvidenceChips evidence={f.evidence} callId={callId} onPlay={onPlay} />
                      </div>
                    </li>
                  ))}
                </ul>
                <p className="subtle" style={{ fontSize: '0.83rem' }}>
                  {t('For coaching, not grading.')}
                </p>
              </section>
            )}

            {review.unsure.length > 0 && (
              <section className="review-section">
                <h3>{t('The AI wasn’t sure about')}</h3>
                <ul className="plain-list">
                  {review.unsure.map((u) => (
                    <li key={u}>{u}</li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}
        {error && <Notice kind="error">{error}</Notice>}
        <div className="review-footer">
          <span>
            {t('Written by AI ({model}) from a machine transcript: it can be wrong.', { model: review.model ?? '' })}
            {review.masked && ` ${t('Personal data was hidden before the transcript was sent.')}`}
          </span>
          {me.role !== 'AGENT' && !review.calls && (
            <button type="button" className="ghost small" onClick={() => void api.rerunReview(callId).then(onChanged)} title={t('Review the call again, e.g. after the scorecard changed')}>
              <RefreshCw size={13} aria-hidden /> {t('Review again')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** Counted from the transcript, not by the AI. */
export function MetricsCard({ metrics, staffName }: { metrics: CallMetricsView; staffName: string }) {
  const share = metrics.staffShare === null ? null : Math.round(metrics.staffShare * 100);
  return (
    <div className="card">
      <div className="card-header">
        <h2>{t('Talk')}</h2>
        <span className="subtle" style={{ fontSize: '0.85rem' }}>
          {t('Counted from the transcript')}
        </span>
      </div>
      <div className="card-body stack">
        {share !== null && (
          <div>
            <div className="talk-bar" role="img" aria-label={t('{name} talked {percent}% of the time', { name: staffName, percent: share })}>
              <span className="staff" style={{ width: `${share}%` }} />
              <span className="other" style={{ width: `${100 - share}%` }} />
            </div>
            <div className="talk-legend">
              <span>
                <span className="swatch outgoing" /> {staffName} {share}%
              </span>
              <span>
                {t('Other side {percent}%', { percent: 100 - share })} <span className="swatch incoming" />
              </span>
            </div>
          </div>
        )}
        <div className="metric-grid">
          <div>
            <span className="tile-label">{t('Longest monologue')}</span>
            <span className="tabular">
              {clock(metrics.longestMonologueMs.staff)} / {clock(metrics.longestMonologueMs.other)}
            </span>
          </div>
          <div>
            <span className="tile-label">{t('Questions asked')}</span>
            <span className="tabular">
              {metrics.questions.staff} / {metrics.questions.other}
            </span>
          </div>
          <div>
            <span className="tile-label">{t('Interruptions')}</span>
            <span className="tabular">
              {metrics.interruptions.staff} / {metrics.interruptions.other}
            </span>
          </div>
        </div>
        <p className="subtle" style={{ fontSize: '0.83rem' }}>
          {t('{name} / the other side.', { name: staffName })}
        </p>
      </div>
    </div>
  );
}
